import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Stack, Typography } from '@mui/material';
import { AssignmentIcon, DataGrid } from '@orijinworks/frontend-commons';
import { NavyMainTypography } from '../../../Feeds/Feeds.style';
import {
  formatLastRefreshedText,
  shouldShowPreviewEmptyState,
} from '../../utils/reentry-planning.util';
import { useReentryPlansTableHook } from './useReentryPlansTable';
import {
  DataGridWrapper,
  PreviewEmptyStateContent,
  PreviewEmptyStateWrapper,
} from './ReentryPlansTable.styles';
import {
  REENTRY_PLANNING_TABS,
  REENTRY_PLANS_LIST_SCOPES,
  REENTRY_PLANS_PAGE_SIZE_OPTIONS,
  REENTRY_CREATE_NEW_PLAN_BUTTON_ID,
  REENTRY_PREVIEW_EMPTY_STATE_HEADER,
  REENTRY_PREVIEW_EMPTY_STATE_BODY,
} from '../../constants/reentry-planning.constants';

const buildColumns = (locationOptions = []) => [
  {
    name: 'LearnerCell',
    preset: 'LearnerCell',
    filter: {
      type: 'LearnerCell',
    },
    displayName: 'Resident',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'preDefined',
    sortByProp: 'learnerName',
  },

  {
    name: 'location',
    displayName: 'Locations',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: false,
    isVisible: true,
    hidden: true,
    type: 'value',
    filter: {
      type: 'MultiTagCell',
    },
    options: locationOptions,
  },
  {
    name: 'status',
    displayName: 'Plan Status',
    isSortable: false,
    isFilterable: false,
    hidden : true,
    type: 'value',
    isVisible: false,
    sortByProp: 'status',
  },
  {
    name: 'editors',
    displayName: 'Editors',
    isSortable: false,
    isFilterable: false,
    type: 'value',
    isVisible: true,
  },
  {
    name: 'action',
    displayName: 'Action',
    type: 'override',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
  },
];

/** Shared plans grid for every Re-Entry Planning tab; `listScope` selects which plans the API returns. */
const ReentryPlansTable = ({
  entitledPlanTypes,
  lastRefreshed,
  listScope = REENTRY_PLANS_LIST_SCOPES.ALL,
  onRefreshHandler,
  title = REENTRY_PLANNING_TABS.ALL_PLANS,
}) => {
  const {
    plans,
    totalCount,
    isLoading,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onFilterChange,
    reloadPlans,
    locationFilterOptions,
    canViewPlanAction,
    canCreatePlan,
    handleCreateNewPlan,
  } = useReentryPlansTableHook({ entitledPlanTypes, listScope, refreshSignal: lastRefreshed });

  const columns = useMemo(
    () =>
      buildColumns(locationFilterOptions).filter(
        (column) => canViewPlanAction || column.name !== 'action',
      ),
    [canViewPlanAction, locationFilterOptions],
  );

  const handleRefresh = () => {
    onRefreshHandler?.();
    reloadPlans();
  };

  const createPlanButtonProps = canCreatePlan
    ? {
        text: 'Create New Plan',
        variant: 'contained',
        'data-pendo-id': REENTRY_CREATE_NEW_PLAN_BUTTON_ID,
        onClick: handleCreateNewPlan,
      }
    : undefined;

  const showPreviewEmptyState = shouldShowPreviewEmptyState({
    entitledPlanTypes,
    totalCount,
    isLoading,
    listScope,
  });

  const previewEmptyStateOverlay = (
    <PreviewEmptyStateWrapper
      role="status"
      aria-live="polite"
      data-testid="reentry-preview-empty-state"
    >
      <PreviewEmptyStateContent>
        <AssignmentIcon aria-hidden="true" sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }} color="text.primary">
          {REENTRY_PREVIEW_EMPTY_STATE_HEADER}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {REENTRY_PREVIEW_EMPTY_STATE_BODY}
        </Typography>
      </PreviewEmptyStateContent>
    </PreviewEmptyStateWrapper>
  );

  const emptyStateGridProps = showPreviewEmptyState
    ? {
        showColumnHeaders: false,
        showPagination: false,
        filterByDisabled: true,
        refreshDisabled: true,
        columnVisibilityDisabled: true,
        noRowsOverlay: previewEmptyStateOverlay,
      }
    : {};

  return (
    <Stack
      height="100%"
      minWidth={0}
      overflowY="auto"
      backgroundColor="white"
      direction="column"
      spacing={2}
      boxSizing="border-box"
      padding="20px"
    >
      <Stack spacing={0.5} flexShrink={0}>
        <NavyMainTypography variant="h1">{title}</NavyMainTypography>
        <Typography variant="body2" color="text.secondary">
          {formatLastRefreshedText(lastRefreshed)}
        </Typography>
      </Stack>
      <DataGridWrapper>
        {/*
          Keep the DataGrid mounted even for the Preview Empty State so its built-in
          "Filter by" toolbar stays visible at 0 rows (CC-15220). While the empty state
          shows, opt-in DataGrid display props hide headers/pagination, native-disable
          Filter by / refresh / column visibility, and render the empty copy via noRowsOverlay.
        */}
        <DataGrid
          columns={columns}
          data={plans}
          pageSize={pageSize}
          pageSizeOptions={REENTRY_PLANS_PAGE_SIZE_OPTIONS}
          paginationMode="server"
          scrollMode="page"
          isLoading={isLoading}
          isAllSelected={false}
          showSelection={false}
          onSelectionChange={() => {}}
          serverSidePaginationProps={{
            onPageChange,
            onPageSizeChange,
            onSortChange,
            onFilterChange,
            totalRecordsCount: totalCount,
          }}
          onRefreshHandler={handleRefresh}
          primaryButtonProps={createPlanButtonProps}
          tableSx={{
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            tableLayout: 'fixed',
            height: 'auto !important',
          }}
          tdStyles={{
            LearnerCell: {
              width: 350,
              maxWidth: 350,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
            action: {
              width: 140,
              minWidth: 140,
              maxWidth: 140,
              textAlign: 'center',
            },
          }}
          {...emptyStateGridProps}
        />
      </DataGridWrapper>
    </Stack>
  );
};

ReentryPlansTable.propTypes = {
  entitledPlanTypes: PropTypes.arrayOf(PropTypes.string),
  lastRefreshed: PropTypes.instanceOf(Date),
  listScope: PropTypes.oneOf(Object.values(REENTRY_PLANS_LIST_SCOPES)),
  onRefreshHandler: PropTypes.func,
  title: PropTypes.string,
};

export default ReentryPlansTable;
